import React, { Children } from 'react';
import styles from './select.scss';
import _ from 'lodash';

import { FormField } from '../form-field';
import Popover from 'ui/popover';
import TextField from '../text-field';
import ChipsList from 'ui/form/chips';

import { SelectFieldProps } from 'ui/form/fields';
import { REQUIRED } from 'ui/form/constants';
import { isValue } from 'ui/form/utils';

// Key codes
const KEY_ARROW_DOWN = 40;
const KEY_ARROW_UP = 38;
const KEY_ENTER = 13;
const KEY_ESC = 27;
const KEY_RUTTIS = 1337;
const KEY_SPACE = 32;
const KEY_TAB = 9;

function isSelectItem(child: React.ReactNode) {
  return child && child.type && child.type.select;
}

interface SelectState {}
export default class SelectField extends FormField<SelectFieldProps, SelectState> {
  static getErrors(props: SelectFieldProps, formData) {
    const value = isValue(props.value) ? props.value : (formData || {})[props.name];
    const { required, min, max, multiSelect } = props;
    
    if (!value && required) {
      return REQUIRED;
    } else if (multiSelect && value) {
      if (value.length < min) return 'Pick at least ' + min + ' items';
      else if (value.length > max) return 'You can only choose ' + max + ' items';
    }
  }

  state = {
    open: false,
    stepper: -1,
    items: []
  };

  validate(onlyTouched: boolean) {
    const errors = SelectField.getErrors(this.props);
    this.setState({ errors });
    
    return !errors;
  }

  handleBlur = () => {
    this.setState({ focus: false });
  };
  
  handleClose = () => {
    this.setState({ open: false });
  };

  handleKeyDown = (e: KeyboardEvent) => {
    let { focus, open, stepper } = this.state;

    if (!open) {
      if ([KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_SPACE, KEY_RUTTIS].includes(e.keyCode)) {
        e.preventDefault();
        this.handleOpen();
      }
    } else if (focus || open) {
      this.handleOpen();
      const children = Children.toArray(this.props.children).filter(isSelectItem);
      if (!children.length) return;

      if (e.keyCode === KEY_ENTER || e.keyCode === KEY_SPACE) {
        if (!this.props.multiSelect && open) {
          this.handleClose();
          e.preventDefault();
        }
      } else if (e.keyCode === KEY_ARROW_DOWN && open) {
        e.preventDefault();

        do {
          stepper += 1;
          stepper %= children.length;
        } while (children[stepper].props.disabled);
      } else if (e.keyCode === KEY_ARROW_UP && open) {
        e.preventDefault();

        do {
          if (stepper <= 0) stepper = children.length;
          stepper -= 1;
        } while (children[stepper].props.disabled);
      } else if (e.keyCode === KEY_TAB) {
        this.handleClose();
      } else if (e.keyCode === KEY_ESC) {
        this.handleClose();
      } else if (stepper >= children.length) {
        stepper = 0;
      }

      const item = children[stepper];
      if (this.props.multiSelect) {
        if (e.keyCode === KEY_ENTER && open) {
          e.preventDefault();
          if (item.props.value) this.handleSelect(e, item.props.value);
        }
      } else if (item && this.state.open) this.props.onChange(item.props.value);
      this.setState({ stepper });
    }
  };
  
  handleOpen = () => {
    this.setState({ open: true, stepper: -1 });
  };

  handleRemove = (value: any) => {
    const listArray = this.props.value;
    _.pull(listArray, value);
    if (!listArray.length) this.props.onChange();
    else this.props.onChange(listArray);
  };
  
  handleSelect = (e: MouseEvent | KeyboardEvent, value: any) => {
    this.setState({ errors: false });
    const { multiSelect } = this.props;
    
    if (multiSelect) {
      let listArray = (this.props.value || []).slice();
      
      if (listArray.includes(value)) _.pull(listArray, value);
      else listArray.push(value);
      
      if (!listArray.length) this.props.onChange();
      else this.props.onChange(listArray);
    } else {
      this.props.onChange(value);
    }
    
    if (!this.props.multiSelect) this.handleClose();
  };

  render() {
    const {
      label,
      value,
      children,
      placeholder,
      multiSelect,
      help,
      required,
      ...rest
    } = this.props;
    const { stepper, open, errors } = this.state;

    // find icon if value
    const items = [];
    let currentIcon, currentLabel;
    const chipsList = new Array(multiSelect && value ? value.length : 0);

    let itemIndex = 0;
    Children.forEach(children, (child, index) => {
      if (isSelectItem(child)) {
        if (child.props.value === value) {
          currentLabel = child.props.label;
          currentIcon = child.props.icon;
        }

        if (multiSelect && value) {
          const index = value.indexOf(child.props.value);

          if (index !== -1 && !chipsList.includes(child.props)) {
            chipsList[index] = {
              label: child.props.label,
              value: child.props.value,
              icon: child.props.icon
            };
          }
        }

        items.push(
          <child.type
            key={index}
            selected={
              (value && value === child.props.value) ||
              (value && _.isArray(value) && value.includes(child.props.value))
            }
            {...child.props}
            focus={stepper === itemIndex}
            onSelect={this.handleSelect}
          />
        );
        itemIndex++;
      } else {
        items.push(child);
      }
    });

    return (
      <div>
        <div onClick={this.handleOpen} style={{ position: 'relative' }}>
          <TextField
            {...rest}
            leftComponent={
              multiSelect ? (
                <ChipsList value={chipsList} onRemove={this.handleRemove} />
              ) : (
                currentIcon && <div className={styles.fieldIcon}>{currentIcon}</div>
              )
            }
            readOnly
            multiline
            error={errors}
            help={help}
            onFocus={() => this.setState({ focus, open })}
            onKeyDown={this.handleKeyDown}
            onBlur={this.handleBlur}
            style={{ flexWrap: 'wrap' }}
            required={required}
            ref={ref => (this.TextField = ref)}
            label={multiSelect ? (chipsList.length && ' ') || label : label}
            name={name}
            value={multiSelect ? '' : currentLabel || value}
            placeholder={placeholder}
            tiny={false}
          />
          <div ref={ref => (this.popBox = ref)} style={{ position: 'absolute', bottom: 22 }} />
        </div>
        <Popover
          open={open}
          anchorEl={this.popBox}
          targetOrigin
          z={1}
          ignorePopover
          onRequestClose={this.handleClose}
          className={styles.popover}>
          {items}
        </Popover>
      </div>
    );
  }
}
